import axios from "axios";
import lodash from "lodash";
import Tag from "@/models/count/tag";
import { formatDateToSqlDateTime } from "@/helpers/date-time";
import { default as Vuex, Module, ActionContext } from "vuex";
import { restApi } from "@/rest-api";
import RootState from "@/store/root-state";

export type AppState =
  | "menu"
  | "connecting"
  | "connected" //connected and scanning
  | "stopping_before_counting"
  | "stopped_before_counting"
  | "counting" //tags were cleared so count button was enabled and here we are
  | "stopping_counting"
  | "stopped_counting" //stopped from counting, can save or resume from here (could also clear)
  | "resuming_counting"
  | "saving"
  | "saved";

class CountModuleState {
  [key: string]: any;
  webSocket: WebSocket = null;
  appState: AppState = null;
  tags: Tag[] = [];
  mapSgtin: { [key: string]: number } = {};
  countButtonBackoff: boolean = false;
  error: boolean = false;
  sgln: string = null;
}

let countButtonBackoffDebounced: any = null;

class CountModule implements Module<CountModuleState, any> {
  namespaced = true;
  state: CountModuleState = new CountModuleState();
  mutations = {
    UPDATE_SGLN(state: CountModuleState, payload: string) {
      state.sgln = payload;
    },
    SET_WEB_SOCKET(state: CountModuleState, payload: WebSocket) {
      state.webSocket = payload;
    },
    SET_APP_STATE(state: CountModuleState, payload: AppState) {
      state.error = false;
      state.appState = payload;
    },
    ADD_TAGS(state: CountModuleState, payload: string[]) {
      payload.forEach(sgtin => {
        if (state.mapSgtin[sgtin] === undefined) {
          state.mapSgtin[sgtin] = 1;

          state.tags.push(new Tag(formatDateToSqlDateTime(), "batchNo", sgtin, null, null, null));
        } else {
          state.mapSgtin[sgtin]++;
        }
      });
    },
    CLEAR_TAGS(state: CountModuleState) {
      state.tags = [];
      state.mapSgtin = {};
    },
    SET_COUNT_BUTTON_BACKOFF(state: CountModuleState, payload: boolean) {
      state.countButtonBackoff = payload;
    },
    FLAG_ERROR(state: CountModuleState) {
      state.error = true;
    },
    CLEAR_ERROR(state: CountModuleState) {
      state.error = false;
    }
  };
  actions = {
    async connect(context: ActionContext<CountModuleState, any>, o: { router: any; jumpToCountingState: boolean }) {
      o = o || { router: null, jumpToCountingState: false };
      if (o.jumpToCountingState) {
        context.commit("SET_APP_STATE", "resuming_counting");
      } else {
        context.commit("SET_APP_STATE", "connecting");
      }

      let address = context.rootState.appSettingsModule.appSettings.hardwareServerAddress;
      let restfulPort = context.rootState.appSettingsModule.appSettings.hardwareServerRestfulPort;
      let readerPort = context.rootState.appSettingsModule.appSettings.hardwareServerReaderPort;

      let protocol = context.rootState.appSettingsModule.appSettings.getRestfulProtocol();

      //let sglnUrl = `https://${address}:${restfulPort}${restApi.apiRfidSgln}`;
      let sglnUrl = `${protocol}://${address}:${restfulPort}${restApi.apiRfidSgln}`;

      context.commit("statusBarModule/SET_MESSAGE", "Connecting...", {
        root: true
      });
      try {
        let response = await axios.get(sglnUrl, {
          timeout: 10000
        });

        context.commit("countModule/UPDATE_SGLN", response.data, { root: true });
      } catch (error) {
        context.commit(
          "statusBarModule/SET_MESSAGE",
          "Failed to connect: Failed to obtain SGLN. Ensure wifi is connected to Dimssa Gantry",
          { root: true }
        );
        context.commit("FLAG_ERROR");
        return;
      }

      context.commit("statusBarModule/SET_MESSAGE", "Connected. Opening web socket.", {
        root: true
      });
      try {
        //create WebSocket
        //let webSocket: WebSocket =
        //commit("SET_WEB_SOCKET", webSocket)
        let wsProtocol = context.rootState.appSettingsModule.appSettings.getWebSocketProtocol();
        let webSocket: WebSocket = new WebSocket(`${wsProtocol}://${address}:${readerPort}`);
        webSocket.onopen = async event => {
          context.commit("statusBarModule/SET_MESSAGE", "Web Socket opened. Starting Reader.", { root: true });
          //start reader
          try {
            let response = await axios.get(
              //`https://${address}:${restfulPort}${restApi.apiGantryStart}`,
              `${protocol}://${address}:${restfulPort}${restApi.apiGantryStart}`,
              {
                timeout: 10000
              }
            );

            if (response.data === false) {
              context.commit("statusBarModule/SET_MESSAGE", "Failed to connect: Error starting reader.", {
                root: true
              });
              return;
            }

            // context.commit("SET_COUNT_BUTTON_BACKOFF", true);
            // lodash.debounce(() => {
            //   context.commit("SET_COUNT_BUTTON_BACKOFF", false);
            // }, 500)();
            if (o.jumpToCountingState) {
              context.commit("SET_APP_STATE", "counting");
            } else {
              context.commit("SET_COUNT_BUTTON_BACKOFF", true);
              if (countButtonBackoffDebounced) {
                //cancel timer if it exists otherwise it can interfere and enable Count too soon
                countButtonBackoffDebounced.cancel();
              }
              countButtonBackoffDebounced = lodash.debounce(() => {
                context.commit("SET_COUNT_BUTTON_BACKOFF", false);
              }, 5000);
              countButtonBackoffDebounced();
              context.commit("SET_APP_STATE", "connected");
            }
            context.commit(
              "statusBarModule/SET_MESSAGE",
              //"Web Socket opened. Starting Reader.",
              "Scanning...",
              { root: true }
            );

            // lodash.debounce(() => {
            //   router.push({ path: "scan" });
            // }, 2000)();
            if (o.router) {
              o.router.push({ path: "scan" });
            }
          } catch (error) {
            context.commit("statusBarModule/SET_MESSAGE", "Failed to connect: Error starting reader.", {
              root: true
            });
            context.commit("FLAG_ERROR");
          }
        };
        webSocket.onerror = event => {
          context.commit("statusBarModule/SET_MESSAGE", "Failed to connect: Web socket error.", {
            root: true
          });
          context.commit("FLAG_ERROR");
          console.log("webSocket onerror");
        };
        webSocket.onmessage = message => {
          const sgtins = message.data
            .split(" ")
            .join("") //remove all spaces that might be in the line with the sgtin
            .split("\n");

          context.commit("ADD_TAGS", sgtins.filter((value: string) => value.length > 0));
        };
        webSocket.onclose = event => {
          console.log("webSocket onclose");
        };
        context.commit("SET_WEB_SOCKET", webSocket);
      } catch (error) {
        //move this catch rather to webSocket.onerror
        context.commit("statusBarModule/SET_MESSAGE", "Failed to connect: Failed to open web socket", { root: true });
        return;
      }

      //get sgln from pi
      //if success then open websocket to reader
      //if success then start reader
      //if success then set timer for 2 seconds
      //go to scan view
    },
    async startCount(context: ActionContext<CountModuleState, any>) {
      // let address =
      //   context.rootState.appSettingsModule.appSettings.hardwareServerAddress;
      // let restfulPort =
      //   context.rootState.appSettingsModule.appSettings
      //     .hardwareServerRestfulPort;
      // let url = `https://${address}:${restfulPort}${restApi.apiGantryStart}`;
      context.commit("CLEAR_ERROR");

      //if state is "connected" then set state to "counting" (count button is disabled so long as tags were found in "connected" state)
      if (context.state.appState === "connected") {
        context.commit("SET_APP_STATE", "counting");
      }

      if (context.state.appState === "stopped_counting") {
        context.dispatch("connect", { jumpToCountingState: true });
      }

      //if state is "stopped_before_counting" then connect reader and set state to "connecting"
      if (context.state.appState === "stopped_before_counting") {
        context.dispatch("connect");
      }

      if (context.state.appState === "resuming_counting" && context.state.error) {
        context.dispatch("connect", { jumpToCountingState: true });
      }
    },
    async stopCount(context: ActionContext<CountModuleState, any>) {
      if (countButtonBackoffDebounced) {
        countButtonBackoffDebounced.flush();
      }

      if (context.state.appState === "connected" || context.state.tags.length === 0) {
        context.commit("SET_APP_STATE", "stopping_before_counting");
      } else if (context.state.appState === "counting") {
        context.commit("SET_APP_STATE", "stopping_counting");
      }

      context.commit("statusBarModule/SET_MESSAGE", "Stopping scan...", {
        root: true
      });

      let address = context.rootState.appSettingsModule.appSettings.hardwareServerAddress;
      let restfulPort = context.rootState.appSettingsModule.appSettings.hardwareServerRestfulPort;
      let protocol = context.rootState.appSettingsModule.appSettings.getRestfulProtocol();
      //let url = `https://${address}:${restfulPort}${restApi.apiGantryStop}`;
      let url = `${protocol}://${address}:${restfulPort}${restApi.apiGantryStop}`;

      try {
        let response = await axios.get(url, {
          timeout: 10000
        });

        if (context.state.appState === "stopping_before_counting") {
          context.commit("SET_APP_STATE", "stopped_before_counting");
        } else if (context.state.appState === "stopping_counting") {
          context.commit("SET_APP_STATE", "stopped_counting");
        }

        context.commit("statusBarModule/SET_MESSAGE", "Scanning stopped.", {
          root: true
        });
      } catch (error) {
        context.commit("statusBarModule/SET_MESSAGE", "Failed to stop scan.", {
          root: true
        });
        context.commit("FLAG_ERROR");
      }
    },
    async saveResults(context: ActionContext<CountModuleState, any>) {

      // let errorSavingToPi = false;
      // let errorSavingToCloud = false;
      // let errorMessagePi = "";
      // let errorMessageCloud = "";

      if (context.state.appState === "counting") {
        //attempt to stop gantry
        let address = context.rootState.appSettingsModule.appSettings.hardwareServerAddress;
        let restfulPort = context.rootState.appSettingsModule.appSettings.hardwareServerRestfulPort;
        let protocol = context.rootState.appSettingsModule.appSettings.getRestfulProtocol();
        let url = `${protocol}://${address}:${restfulPort}${restApi.apiGantryStop}`;

        axios.get(url);
      }

      context.commit("SET_APP_STATE", "saving");

      //let batchNo = Date.now().toString();
      let result = { sgln: context.state.sgln, tags: context.state.tags };

      let address = context.rootState.appSettingsModule.appSettings.onsiteServerAddress;
      let port = context.rootState.appSettingsModule.appSettings.onsiteServerRestfulPort;
      let protocol = context.rootState.appSettingsModule.appSettings.getRestfulProtocol();
      let url = `${protocol}://${address}:${port}${restApi.saveGantryResults}`;

      console.log("submitting results: " + url);
      //console.log(JSON.stringify(result));
      try {
        let response = await fetch(url, {
          method: "POST",
          //mode: "no-cors",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(result)
        });

        console.log(`response status: ${response.status}`);
        console.log(`response.ok: ${response.ok}`);
        if (!response.ok) {
          // errorSavingToPi = true;
          // errorMessagePi = `/Gantry/SubmitResults failed. Response code ${response.status}`;
          context.commit("FLAG_ERROR");
          throw Error(`/Gantry/SubmitResults failed. Response code ${response.status}`);
        } else {
          context.commit("SET_APP_STATE", "saved");
        }
        console.log("fetch response:" + JSON.stringify(response));
      } catch (error) {
        console.log("error submitting results", error);
        context.commit("FLAG_ERROR");
        // errorSavingToPi = true;
        // errorMessagePi = error;
      }

      // address = "gigalot.systems";
      // protocol = context.rootState.appSettingsModule.appSettings.getRestfulProtocol();
      // url = `${protocol}://${address}${restApi.saveGantryResults}`;
      // console.log("Cloud, submitting results: " + url);

      // let aResultPaulWouldPrefer = result.tags.map(tag => {
      //   return {
      //     Guid: tag.Guid,
      //     ScannedTime: tag.ScannedTime,
      //     BatchNo: tag.BatchNo,
      //     AnimalSGTIN: tag.SGTIN,
      //     AnimalSGLN: result.sgln,
      //     GPSLat: tag.GPSLat,
      //     GPSLong: tag.GPSLong,
      //     ScannedAntenna: tag.ScannedAntenna
      //   };
      // });

      // try {
      //   let response = await fetch(url, {
      //     method: "POST",
      //     //mode: "no-cors",
      //     headers: {
      //       "Content-Type": "application/json"
      //     },
      //     body: JSON.stringify(aResultPaulWouldPrefer)
      //   });

      //   console.log(`Cloud, response status: ${response.status}`);
      //   console.log(`Cloud, response.ok: ${response.ok}`);
      //   if (!response.ok) {
      //     errorSavingToCloud = true;
      //     errorMessageCloud = `Cloud, response not ok: ${response.status}`;
      //     console.log(`Cloud, response not ok: ${response.status}`);
      //     // throw Error(
      //     //   `/Gantry/SubmitResults failed. Response code ${response.status}`
      //     // );
      //   } else {
      //     //context.commit("SET_APP_STATE", "saved");
      //     //success saving to cloud
      //   }
      //   console.log("Cloud, fetch response:" + JSON.stringify(response));
      // } catch (error) {
      //   console.log("Cloud, error submitting results", error);
      //   //context.commit("FLAG_ERROR");
      //   errorSavingToCloud = true;
      //   errorMessageCloud = error;
      // }

      // if (errorSavingToPi && errorSavingToCloud) {
      //   context.commit("FLAG_ERROR");
      //   context.commit("statusBarModule/SET_MESSAGE", "Error: " + errorMessagePi, {
      //     root: true
      //   });
      // } else {
      //   context.commit("SET_APP_STATE", "saved");
      //   if (!errorSavingToCloud) {
      //     context.commit("statusBarModule/SET_MESSAGE", "Results saved to cloud.", {
      //       root: true
      //     });
      //   }
      // }
    },
    restoreSession(context: ActionContext<CountModuleState, any>) {
      console.log("countModule/restoreSession");
      context.commit("SET_WEB_SOCKET", null);
      if (
        //clear state if app wasn't counting before last close
        context.state.appState === null ||
        context.state.appState === "menu" ||
        context.state.appState === "connecting" ||
        context.state.appState === "stopped_before_counting" ||
        context.state.appState === "connected" ||
        context.state.appState === "stopping_before_counting"
      ) {
        context.commit("SET_APP_STATE", "menu");
        context.commit("CLEAR_TAGS");
        context.commit("UPDATE_SGLN", null);
      } else if (
        //clear state if app was counting before last close but there were no tags scanned
        context.state.tags.length == 0 &&
        (context.state.appState === "counting" ||
          context.state.appState === "resuming_counting" ||
          context.state.appState === "stopping_counting" ||
          context.state.appState === "stopped_counting")
      ) {
        //attempt to stop scanner if state was counting, resuming_counting, or stopping_counting because the scanner could still be running
        if (context.state.appState !== "stopped_counting") {
          let address = context.rootState.appSettingsModule.appSettings.hardwareServerAddress;
          let restfulPort = context.rootState.appSettingsModule.appSettings.hardwareServerRestfulPort;
          let protocol = context.rootState.appSettingsModule.appSettings.getRestfulProtocol();
          //let url = `https://${address}:${restfulPort}${restApi.apiGantryStop}`;
          let url = `${protocol}://${address}:${restfulPort}${restApi.apiGantryStop}`;

          axios.get(url, {
            timeout: 10000
          });
        }
        context.commit("SET_APP_STATE", "menu");
        context.commit("CLEAR_TAGS");
        context.commit("UPDATE_SGLN", null);
      } else if (
        //if app was busy counting and had tags then attempt to stop scanner and set state to stopped_counting
        context.state.tags.length > 0 &&
        (context.state.appState === "counting" ||
          context.state.appState === "resuming_counting" ||
          context.state.appState === "stopping_counting")
      ) {
        let address = context.rootState.appSettingsModule.appSettings.hardwareServerAddress;
        let restfulPort = context.rootState.appSettingsModule.appSettings.hardwareServerRestfulPort;
        let protocol = context.rootState.appSettingsModule.appSettings.getRestfulProtocol();
        //let url = `https://${address}:${restfulPort}${restApi.apiGantryStop}`;
        let url = `${protocol}://${address}:${restfulPort}${restApi.apiGantryStop}`;

        axios.get(url, {
          timeout: 10000
        });

        context.commit("SET_APP_STATE", "stopped_counting");
      } else if (context.state.appState === "saving") {
        context.dispatch("saveResults");
      }

      //if state was counting, stopping_counting, or resuming_counting:
      //send a gantry stop command in case the gantry is still scanning
      //set state to stopped_counting
    }
  };
}

const countModule: CountModule = new CountModule();

export default countModule;
